/* ==========================================================================
   XTRUDE — currency.js
   Geo auto-detect + manual override, live in-page price conversion.
   Store prices are AED; conversion rewrites every "Dhs. X" text node on the
   page (cards, PDP, cart, sticky bars, sentences) at display time.
   Checkout still settles in AED — this is a display layer.
   ========================================================================== */
(() => {
  'use strict';

  const STORAGE_KEY = 'xtrude-currency';
  const GEO_KEY = 'xtrude-country';

  const CURRENCIES = {
    AED: { code: 'AED', label: 'DHS', symbol: 'Dhs.', name: 'UAE Dirham', rate: 1 },
    SAR: { code: 'SAR', label: 'SAR', symbol: 'SR', name: 'Saudi Riyal', rate: 1.0218 },
    QAR: { code: 'QAR', label: 'QTR', symbol: 'QR', name: 'Qatari Riyal', rate: 0.9918 },
  };

  const COUNTRY_TO_CURRENCY = { AE: 'AED', SA: 'SAR', QA: 'QAR' };

  // Matches "Dhs. 1,234.50" with an optional trailing "AED" (money_with_currency)
  const MONEY_RE = /Dhs\.\s*([\d,]+(?:\.\d+)?)(?:\s*AED\b)?/g;
  const SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1 };

  let current = 'AED';
  let observer = null;

  const fmt = (aedNumeric) => {
    const c = CURRENCIES[current];
    const value = (aedNumeric * c.rate).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${c.symbol} ${value}`;
  };

  // Render one text node from its pristine AED text. The original is kept on
  // the node itself; if some other script wrote fresh AED text since our last
  // render, that new text becomes the original.
  const renderNode = (node) => {
    const val = node.nodeValue;
    if (node.__xcShown !== undefined && val !== node.__xcShown) {
      node.__xcOriginal = val;
    } else if (node.__xcOriginal === undefined) {
      node.__xcOriginal = val;
    }
    const out = node.__xcOriginal.replace(MONEY_RE, (_, num) =>
      fmt(parseFloat(num.replace(/,/g, '')))
    );
    node.__xcShown = out;
    if (out !== val) node.nodeValue = out;
  };

  const convertAll = () => {
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (node.parentElement && SKIP_TAGS[node.parentElement.tagName]) {
          return NodeFilter.FILTER_REJECT;
        }
        return node.__xcOriginal !== undefined || node.nodeValue.indexOf('Dhs.') !== -1
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      },
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(renderNode);
    // Drain mutations caused by our own writes so the observer doesn't loop
    if (observer) observer.takeRecords();
  };

  const announce = () => {
    document.dispatchEvent(
      new CustomEvent('currencyChanged', {
        detail: { code: current, currency: CURRENCIES[current] },
      })
    );
  };

  const apply = (code) => {
    if (!code || !CURRENCIES[code] || code === current) return;
    current = code;
    announce();
    convertAll();
  };

  // Manual pick — persists across sessions and wins over geo detection
  const setCurrency = (code) => {
    if (!CURRENCIES[code]) return;
    localStorage.setItem(STORAGE_KEY, code);
    apply(code);
  };

  // Geo detection only runs when the user hasn't picked manually
  const geoDetect = () => {
    if (localStorage.getItem(STORAGE_KEY)) return;

    const cached = sessionStorage.getItem(GEO_KEY);
    if (cached) {
      apply(COUNTRY_TO_CURRENCY[cached]);
      return;
    }

    fetch('https://ipapi.co/json/')
      .then((r) => r.json())
      .then((data) => {
        if (!data || !data.country_code) return;
        sessionStorage.setItem(GEO_KEY, data.country_code);
        apply(COUNTRY_TO_CURRENCY[data.country_code]);
      })
      .catch(() => {});
  };

  const init = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && CURRENCIES[stored]) current = stored;

    convertAll();
    announce();

    // Variant changes and AJAX cart re-renders write fresh AED prices —
    // watch the DOM and convert them as they land.
    observer = new MutationObserver(() => convertAll());
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    geoDetect();
  };

  window.CurrencyManager = {
    setCurrency,
    convertAll,
    getCurrent: () => CURRENCIES[current],
    getAll: () => CURRENCIES,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
